import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import {
  FilePdf,
  DownloadSimple,
  ArrowRight,
  HandHeart,
  Users,
  UserFocus,
} from "@phosphor-icons/react";

/**
 * Free Resources — Begin Again Living Ministries
 *
 * 21 free PDF guides organized in 3 categories (7 each). All PDFs live in
 * /public/resources so they ship as stable static assets with the deployed
 * build and are reachable at https://balministries.org/resources/*.pdf.
 */

const SECTIONS = [
  {
    id: "hospice-families",
    label: "For Hospice Families",
    icon: HandHeart,
    accentClass: "from-primary/12 via-white to-secondary/15",
    intro:
      "Compassionate guidance for families entering, walking through, and honoring the final chapter of a loved one\u2019s journey.",
    resources: [
      {
        id: "hospice-what-to-expect",
        title: "What to Expect When a Loved One Enters Hospice",
        description:
          "A gentle introduction to comfort-focused care, the services hospice may provide, questions families can ask, and meaningful ways to remain present with a loved one.",
        file: "/resources/BALM_What_to_Expect_When_a_Loved_One_Enters_Hospice.pdf",
      },
      {
        id: "hospice-understanding-care-team",
        title: "Understanding the Hospice Care Team",
        description:
          "Learn who may be part of the hospice team and how each discipline supports the patient and family.",
        file: "/resources/BALM_Hospice_Understanding_the_Hospice_Care_Team.pdf",
      },
      {
        id: "hospice-questions-to-ask",
        title: "Questions to Ask Your Hospice Team",
        description:
          "A practical conversation guide for understanding the care plan, comfort measures, medications, visits, and family support.",
        file: "/resources/BALM_Hospice_Questions_to_Ask_Your_Hospice_Team.pdf",
      },
      {
        id: "hospice-preparing-children",
        title: "Preparing Children for a Loved One\u2019s Decline",
        description:
          "Age-appropriate guidance for communicating honestly, providing reassurance, and including children without forcing participation.",
        file: "/resources/BALM_Hospice_Preparing_Children_for_a_Loved_Ones_Decline.pdf",
      },
      {
        id: "hospice-meaningful-conversations",
        title: "Meaningful Conversations Near the End of Life",
        description:
          "Gentle ways to listen, connect, honor preferences, and express the words that matter.",
        file: "/resources/BALM_Hospice_Meaningful_Conversations_Near_the_End_of_Life.pdf",
      },
      {
        id: "hospice-signs-final-days",
        title: "Signs a Loved One May Be Approaching the Final Days",
        description:
          "Common changes families may observe, supportive comfort measures, and reminders about when to contact hospice.",
        file: "/resources/BALM_Hospice_Signs_a_Loved_One_May_Be_Approaching_the_Final_Days.pdf",
      },
      {
        id: "hospice-peaceful-bedside",
        title: "Creating a Peaceful and Meaningful Bedside Environment",
        description:
          "Simple ways to support comfort, dignity, connection, and meaningful family presence.",
        file: "/resources/BALM_Hospice_Creating_a_Peaceful_and_Meaningful_Bedside.pdf",
      },
    ],
  },
  {
    id: "grieving-individuals",
    label: "For Grieving Individuals and Their Supporters",
    icon: Users,
    accentClass: "from-secondary/15 via-white to-primary/10",
    intro:
      "Words, practices, and gentle guidance for those walking through grief \u2014 and for the friends, family, and helpers who walk beside them.",
    resources: [
      {
        id: "grief-10-supportive-things",
        title: "10 Supportive Things to Say to Someone Who Is Grieving",
        description:
          "Ten compassionate statements that communicate presence and care without minimizing, explaining, or attempting to fix someone\u2019s grief.",
        file: "/resources/BALM_10_Supportive_Things_to_Say_to_Someone_Who_Is_Grieving.pdf",
      },
      {
        id: "grief-many-faces",
        title: "Understanding the Many Faces of Grief",
        description:
          "An introduction to the emotional, physical, social, cognitive, and spiritual experiences of grief.",
        file: "/resources/BALM_Grief_Understanding_the_Many_Faces_of_Grief.pdf",
      },
      {
        id: "grief-anticipatory",
        title: "Coping With Anticipatory Grief",
        description:
          "Compassionate support when a serious decline or loss is expected but has not yet occurred.",
        file: "/resources/BALM_Grief_Coping_With_Anticipatory_Grief.pdf",
      },
      {
        id: "grief-holidays",
        title: "Navigating Grief During Holidays and Special Days",
        description:
          "A flexible planning guide for anniversaries, celebrations, traditions, and emotionally significant dates.",
        file: "/resources/BALM_Grief_Navigating_Holidays_and_Special_Days.pdf",
      },
      {
        id: "grief-child-teen",
        title: "Supporting a Grieving Child or Teen",
        description:
          "Honest communication, steady reassurance, age-appropriate choices, and signs that additional support may be needed.",
        file: "/resources/BALM_Grief_Supporting_a_Grieving_Child_or_Teen.pdf",
      },
      {
        id: "grief-overwhelming",
        title: "When Grief Feels Overwhelming: A Gentle Guide",
        description:
          "Grounding techniques, supportive next steps, and crisis information for moments when grief feels unmanageable.",
        file: "/resources/BALM_Grief_When_Grief_Feels_Overwhelming.pdf",
      },
      {
        id: "grief-rituals",
        title: "Remembering Your Loved One Through Meaningful Rituals",
        description:
          "Personal and family practices for honoring memory, values, love, and continuing connection.",
        file: "/resources/BALM_Grief_Remembering_Through_Meaningful_Rituals.pdf",
      },
    ],
  },
  {
    id: "family-caregivers",
    label: "For Family Caregivers",
    icon: UserFocus,
    accentClass: "from-primary/10 via-white to-secondary/20",
    intro:
      "Steady, practical support for the family caregivers holding the load \u2014 protecting your capacity while caring for someone you love.",
    resources: [
      {
        id: "caregiver-wellness-checklist",
        title: "A Caregiver\u2019s Emotional Wellness Checklist",
        description:
          "A compassionate self-check to help caregivers recognize emotional strain, identify immediate needs, and create a practical personal-support plan.",
        file: "/resources/BALM_A_Caregivers_Emotional_Wellness_Checklist.pdf",
      },
      {
        id: "caregiver-burnout",
        title: "Recognizing Caregiver Burnout",
        description:
          "Warning signs of caregiver overload, protective next steps, and guidance about when additional help is needed.",
        file: "/resources/BALM_Caregiver_Recognizing_Caregiver_Burnout.pdf",
      },
      {
        id: "caregiver-asking-family",
        title: "Asking Family Members for Help",
        description:
          "Practical guidance for making clear requests, sharing responsibilities, and holding realistic family conversations.",
        file: "/resources/BALM_Caregiver_Asking_Family_Members_for_Help.pdf",
      },
      {
        id: "caregiver-support-plan",
        title: "Creating a Caregiver Support Plan",
        description:
          "A practical worksheet for organizing daily care, backup assistance, important information, and caregiver recovery.",
        file: "/resources/BALM_Caregiver_Creating_a_Caregiver_Support_Plan.pdf",
      },
      {
        id: "caregiver-boundaries",
        title: "Setting Healthy Boundaries as a Caregiver",
        description:
          "Compassionate ways to communicate limits, protect caregiver capacity, and identify safer alternatives.",
        file: "/resources/BALM_Caregiver_Setting_Healthy_Boundaries.pdf",
      },
      {
        id: "caregiver-five-minute",
        title: "Five-Minute Practices for Caregiver Renewal",
        description:
          "Brief practices for reducing continuous stress and reconnecting with physical, emotional, relational, and spiritual needs.",
        file: "/resources/BALM_Caregiver_Five_Minute_Practices_for_Renewal.pdf",
      },
      {
        id: "caregiver-questions-care-team",
        title: "Questions Caregivers Should Ask the Care Team",
        description:
          "Important questions about symptoms, medications, hands-on care, equipment, safety, respite, and after-hours support.",
        file: "/resources/BALM_Caregiver_Questions_to_Ask_the_Care_Team.pdf",
      },
    ],
  },
];

const ResourceCard = ({ resource, sectionLabel, accentClass }) => (
  <article
    data-testid={`resource-card-${resource.id}`}
    className="flex flex-col bg-white border border-ink-100 rounded-3xl overflow-hidden shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)] hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.18)] hover:-translate-y-1 transition-all duration-300"
  >
    {/* PDF cover preview */}
    <div
      aria-hidden="true"
      className={`aspect-[4/3] bg-gradient-to-br ${accentClass} border-b border-ink-100 flex items-center justify-center relative`}
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none [background-image:radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.6),transparent_60%)]" />
      <div className="relative flex flex-col items-center">
        <FilePdf size={56} weight="duotone" className="text-primary" />
        <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-primary font-semibold bg-white/70 backdrop-blur-sm border border-primary/15 rounded-full px-3 py-1">
          PDF <span className="text-ink-400">&bull;</span> Free Download
        </span>
      </div>
    </div>

    {/* Content */}
    <div className="flex-1 flex flex-col p-6 sm:p-7">
      <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-secondary-dark font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
        {sectionLabel}
      </span>
      <h3
        data-testid={`resource-title-${resource.id}`}
        className="mt-3 font-heading text-xl sm:text-2xl text-ink-900 font-medium leading-snug"
      >
        {resource.title}
      </h3>
      <p className="mt-3 text-ink-700 leading-relaxed text-sm flex-1">
        {resource.description}
      </p>

      <a
        href={resource.file}
        download
        data-testid={`resource-download-${resource.id}`}
        aria-label={`Download the free PDF guide: ${resource.title}`}
        className="mt-6 inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
      >
        <DownloadSimple size={18} weight="bold" />
        Download Free Guide
      </a>
    </div>
  </article>
);

const SectionBlock = ({ section }) => {
  const Icon = section.icon;
  return (
    <section
      id={section.id}
      data-testid={`resources-section-${section.id}`}
      className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100 first:border-t-0"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Category
          </span>
          <div className="mt-5 flex items-start gap-4">
            <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
              <Icon size={26} weight="duotone" />
            </span>
            <div>
              <h2
                data-testid={`resources-section-heading-${section.id}`}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.15]"
              >
                {section.label}
              </h2>
              <p className="mt-3 text-ink-700 text-base sm:text-lg leading-relaxed">
                {section.intro}
              </p>
            </div>
          </div>
        </div>

        <div
          data-testid={`resources-grid-${section.id}`}
          className="mt-10 sm:mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {section.resources.map((r) => (
            <ResourceCard
              key={r.id}
              resource={r}
              sectionLabel={section.label}
              accentClass={section.accentClass}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Resources = () => {
  return (
    <div data-testid="page-resources">
      <Seo
        title="Free Faith-Based Devotional & Grief Resources"
        description="Download free faith-based devotionals, grief workbooks, caregiver guides, and hospice resources from Begin Again Living Ministries — printable PDFs."
      />
      {/* HERO */}
      <section
        data-testid="resources-hero"
        className="relative pt-24 pb-14 sm:pt-32 sm:pb-16 overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-softblue/60 via-white to-secondary/5"
        />
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-secondary/10 blur-3xl -z-10"
        />

        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Free Resources
          </span>
          <h1
            data-testid="resources-hero-title"
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900"
          >
            Free Resources for the Journey
          </h1>
          <p
            data-testid="resources-hero-intro"
            className="mt-6 text-ink-700 text-lg leading-relaxed"
          >
            Compassionate, practical guidance for individuals and families
            navigating hospice, caregiving, grief, and life after loss.
            These complimentary resources are provided by Begin Again Living
            Ministries to help you feel informed, supported, and less alone.
          </p>

          {/* Category jump links */}
          <nav
            aria-label="Jump to resource category"
            data-testid="resources-jump-nav"
            className="mt-10 flex flex-wrap gap-2"
          >
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`resources-jump-${s.id}`}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-ink-100 text-sm text-ink-700 hover:text-primary hover:border-primary/30 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      <div className="pb-4">
        {SECTIONS.map((s) => (
          <SectionBlock key={s.id} section={s} />
        ))}
      </div>

      {/* NEED ADDITIONAL SUPPORT */}
      <section
        data-testid="resources-additional-support"
        className="py-20 sm:py-24 bg-softblue/40 border-y border-ink-100"
      >
        <div className="container-x max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Continue the Journey
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.15]">
            Need Additional Support?
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Begin Again Living Ministries offers compassionate support,
            education, workshops, and speaking programs for individuals,
            families, caregivers, churches, and organizations navigating
            grief, caregiving, and end-of-life transitions.
          </p>
          <Link
            to="/from-diagnosis-to-goodbye"
            data-testid="resources-explore-services-btn"
            className="mt-9 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
          >
            Explore From Diagnosis to Goodbye
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* LEGAL NOTE */}
      <section className="py-14 sm:py-16">
        <div className="container-x max-w-3xl">
          <p
            data-testid="resources-legal-note"
            className="text-xs sm:text-sm text-ink-500 leading-relaxed text-center"
          >
            &copy; 2026 Derrick Hall. All rights reserved. From Diagnosis
            to Goodbye is a proprietary educational and professional training
            platform developed and owned by Derrick Hall. Federal
            trademark application pending. These materials are made
            available by Begin Again Living Ministries with permission.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Resources;
